import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Flag, Lightbulb, Heart, ArrowRight } from 'lucide-react';

const UMaryland: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "University of Maryland";

    return (
        <div className="min-h-screen bg-white font-sans text-[#E03A3E] selection:bg-[#FFD200] selection:text-black">
            {/* Top Bar - Red */}
            <div className="bg-[#E03A3E] text-white py-1 px-6 flex justify-end gap-6 text-xs font-bold uppercase tracking-wider">
                <a href="#" className="hover:text-[#FFD200]">Calendar</a>
                <a href="#" className="hover:text-[#FFD200]">Directory</a>
                <a href="#" className="hover:text-[#FFD200]">Map</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b-4 border-[#FFD200]">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        {/* Globe Icon for UMD */}
                         <div className="w-10 h-10 bg-[#E03A3E] rounded-full flex items-center justify-center text-white font-black text-xl">M</div>
                        <div className="flex flex-col">
                            <span className="font-serif text-2xl font-bold text-[#E03A3E] uppercase tracking-wide leading-none">{schoolName}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#212121] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#E03A3E] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#E03A3E] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#E03A3E] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#E03A3E] transition-colors">Arts</a>
                        <a href="#" className="hover:text-[#E03A3E] transition-colors">Athletics</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#212121] hover:text-[#E03A3E]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#212121]" size={26} />
                    </div>
                </div>
            </nav>

            {/* Hero - Fearless */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1574169208507-84376194878a?q=80&w=2069&auto=format&fit=crop" 
                    alt="McKeldin Mall" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Red Overlay */}
                 <div className="absolute inset-0 bg-[#E03A3E]/30 mix-blend-multiply"></div>

                <div className="absolute bottom-0 left-0 w-full p-12 md:p-20 bg-gradient-to-t from-black/80 to-transparent">
                     <div className="max-w-4xl mx-auto text-white">
                         <h1 className="text-5xl md:text-8xl font-black mb-6 uppercase italic">
                             Fearless Ideas.
                         </h1>
                         <p className="text-xl md:text-2xl mb-10 font-bold max-w-2xl">
                             We turn imagination into innovation and problems into solutions.
                         </p>
                         <Button className="bg-[#FFD200] hover:bg-[#e6bd00] text-black font-bold uppercase tracking-widest px-10 h-14 rounded-full shadow-lg">
                             Discover {schoolName.split(' ').pop()}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats - Maryland Flag Colors */}
            <section className="py-20 px-6 md:px-12 bg-[#212121] text-white">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-8 border-b-4 border-[#E03A3E] bg-[#333]">
                         <Flag size={48} className="text-[#FFD200] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">#19</h3>
                         <p className="font-bold uppercase text-xs tracking-widest">Top Public Research University</p>
                     </div>
                      <div className="p-8 border-b-4 border-[#FFD200] bg-[#333]">
                         <Lightbulb size={48} className="text-[#E03A3E] mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">Innovation</h3>
                         <p className="font-bold uppercase text-xs tracking-widest">A Leader in Entrepreneurship</p>
                     </div>
                      <div className="p-8 border-b-4 border-white bg-[#333]">
                         <Heart size={48} className="text-white mx-auto mb-6" />
                         <h3 className="text-3xl font-black mb-2">Do Good</h3>
                         <p className="font-bold uppercase text-xs tracking-widest">First "Do Good" Campus in the Nation</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-white py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <h2 className="text-4xl font-black text-[#212121] mb-12 uppercase italic">{schoolName} Today</h2>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                         {[
                             "UMD researchers develop new quantum computing materials",
                             "Terrapins celebrate historic win over big ten rival",
                             "New arts district transforming College Park"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-64 mb-6 overflow-hidden relative shadow-md">
                                     <div className="absolute top-0 right-0 bg-[#E03A3E] text-white font-bold p-2 z-10 text-xs uppercase">News</div>
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+50}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="News"/>
                                 </div>
                                 <h3 className="text-xl font-bold text-[#212121] group-hover:text-[#E03A3E] transition-colors leading-tight mb-4">{title}</h3>
                                  <div className="flex items-center gap-2 text-[#E03A3E] font-bold uppercase text-xs tracking-widest group-hover:gap-3 transition-all">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#212121] text-gray-400 py-16 px-6 md:px-12 border-t-8 border-[#FFD200]">
                  <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                           <span className="font-serif font-black text-2xl text-white mb-6 block uppercase tracking-wide">{schoolName}</span>
                          <p className="mb-6">
                              College Park, MD 20742 <br/>
                              301.405.1000
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest text-white">
                          <ul className="space-y-4 hover:[&_a]:text-[#FFD200]">
                              <li><a href="#">Privacy Policy</a></li>
                              <li><a href="#">Web Accessibility</a></li>
                              <li><a href="#">University Policies</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-[#FFD200]">
                              <li><a href="#">Contact Us</a></li>
                              <li><a href="#">Visit</a></li>
                              <li><a href="#">Work at {schoolName.split(' ')[0]}</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-500 border-t border-gray-700 pt-8">
                      © 2026 {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default UMaryland;
